import { getApiUrl, getAuthHeaders, isRecord, readJsonObject } from './api-config';

// Integration endpoints that should not trigger user logout on 401
const INTEGRATION_ENDPOINTS = [
  'zoho/orgs/',
  'zoho/departments/',
  'zoho/contacts/',
  'zoho/status/',
  'zoho/connect/',
  'zoho/auth/',
  'zoho/config/',
  'hubspot/status/',
  'hubspot/connect/',
  'hubspot/pipelines/',
  'salesforce/status/',
  'salesforce/connect/',
  'salesforce/auth/',
  'zendesk/status/',
  'zendesk/connect/',
  'ticketing/zendesk-integrations/',
  'freshdesk/status/',
  'freshdesk/connect/',
  'ticketing/freshdesk-integrations/',
  'drive/files/',
  'drive/unlink/',
  'drive/add-to-agent-folder/',
  'slack/status/',
  'slack/connect/',
  'whatsapp/status/',
  'whatsapp/connect/',
  'messenger/status/',
  'messenger/connect/',
  'instagram/status/',
  'instagram/connect/',
  'zapier/status/',
  'zapier/connect/'
];

// Helper function to check if URL is an integration endpoint
const isIntegrationEndpoint = (url: string): boolean => {
  return INTEGRATION_ENDPOINTS.some(endpoint => url.includes(endpoint));
};

// Trigger global forbidden-access flow so AuthContext can react (logout, toasts, etc.)
const notifyForbiddenAccess = (sourceUrl: string) => {
  window.dispatchEvent(new CustomEvent('forbidden-access', {
    detail: { sourceUrl }
  }));
};

// JWT token decoder utility
const decodeJWT = (token: string) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    const payload: unknown = JSON.parse(jsonPayload);
    return isRecord(payload) ? payload : null;
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
};

// Check if token is expired or will expire soon (within 5 minutes)
const isTokenExpiring = (token: string): boolean => {
  const decoded = decodeJWT(token);
  if (!decoded || typeof decoded.exp !== 'number' || !decoded.exp) return true;
  
  const currentTime = Math.floor(Date.now() / 1000);
  const expirationTime = decoded.exp;
  const timeUntilExpiration = expirationTime - currentTime;
  
  // Consider token expiring if less than 5 minutes remaining
  return timeUntilExpiration < 300;
};

// Refresh token function
const refreshAccessToken = async (): Promise<string | null> => {
  const user = localStorage.getItem('user');
  if (!user) return null;
  
  try {
    const userData: unknown = JSON.parse(user);
    if (!isRecord(userData)) return null;
    const refreshToken = userData.refreshToken;
    
    if (typeof refreshToken !== 'string' || !refreshToken) return null;
    
    console.log('Attempting to refresh token...');
    
    const response = await fetch(getApiUrl('token/refresh/'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        refresh: refreshToken
      })
    });
    
    if (!response.ok) {
      console.error('Refresh token expired or invalid');
      throw new Error('Refresh token expired');
    }
    
    const data = await readJsonObject(response);
    if (typeof data.access !== 'string' || !data.access) {
      throw new Error('Token refresh response is missing an access token');
    }
    console.log('Token refreshed successfully');
    
    // Update stored user data with new access token
    const updatedUserData = {
      ...userData,
      accessToken: data.access
    };
    
    localStorage.setItem('user', JSON.stringify(updatedUserData));
    
    // Dispatch event to notify AuthContext of token update
    window.dispatchEvent(new CustomEvent('token-refreshed', {
      detail: { newToken: data.access, userData: updatedUserData }
    }));
    
    return data.access;
  } catch (error) {
    console.error('Error refreshing token:', error);
    return null;
  }
};

// Global flag to prevent concurrent refresh attempts
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

// Enhanced fetch function with token handling
export const apiRequest = async (
  url: string, 
  options: RequestInit = {},
  authRequired: boolean = true
): Promise<Response> => {
  // Get current user data
  const user = localStorage.getItem('user');
  let accessToken: string | null = null;
  
  if (user && authRequired) {
    try {
      const userData: unknown = JSON.parse(user);
      accessToken = isRecord(userData) && typeof userData.accessToken === 'string'
        ? userData.accessToken
        : null;
      
      console.log('Current token expiring check:', accessToken ? isTokenExpiring(accessToken) : 'No token');
      
      // Check if token is expiring and refresh if needed
      if (accessToken && isTokenExpiring(accessToken)) {
        console.log('Token is expiring, refreshing...');
        
        if (!isRefreshing) {
          isRefreshing = true;
          refreshPromise = refreshAccessToken();
        }
        
        const newToken = await refreshPromise;
        if (newToken) {
          accessToken = newToken;
          console.log('Using refreshed token for request');
        } else {
          console.log('Failed to refresh token');
        }
        
        isRefreshing = false;
        refreshPromise = null;
      }
    } catch (error) {
      console.error('Error parsing user data:', error);
    }
  }
  
  // Prepare headers - handle FormData specially
  let headers: HeadersInit = {};
  
  if (authRequired && accessToken) {
    // For FormData, only add Authorization header, let browser set Content-Type
    if (options.body instanceof FormData) {
      headers = {
        'Authorization': `Bearer ${accessToken}`
      };
    } else {
      // For regular requests, use full auth headers
      headers = getAuthHeaders(accessToken);
    }
  } else if (!(options.body instanceof FormData)) {
    // For non-auth requests that aren't FormData, set basic headers
    headers = options.headers || {};
  }
  
  // Merge with any additional headers from options, but don't override Content-Type for FormData
  if (options.headers) {
    if (options.body instanceof FormData) {
      // For FormData, only merge non-Content-Type headers
      Object.entries(options.headers).forEach(([key, value]) => {
        if (key.toLowerCase() !== 'content-type') {
          (headers as Record<string, string>)[key] = value as string;
        }
      });
    } else {
      // For regular requests, merge all headers
      headers = {
        ...headers,
        ...options.headers
      };
    }
  }
  
  // Make the request
  const response = await fetch(url, {
    ...options,
    headers
  });
  
  // Handle 401 responses
  if (response.status === 401 && authRequired) {
    console.log('Received 401, checking if integration endpoint...');
    
    // Check if this is an integration endpoint
    const isIntegration = isIntegrationEndpoint(url);
    
    if (isIntegration) {
      console.log('401 from integration endpoint, not triggering logout');
      // For integration endpoints, return the 401 response without triggering logout
      // This allows integration components to handle the authentication failure appropriately
      return response;
    }
    
    console.log('401 from user endpoint, attempting token refresh...');
    
    // Try to refresh token if not already refreshing (only for non-integration endpoints)
    if (!isRefreshing) {
      isRefreshing = true;
      const newToken = await refreshAccessToken();
      isRefreshing = false;
      
      if (newToken) {
        console.log('Token refreshed, retrying original request');
        // Retry the original request with new token
        let retryHeaders: HeadersInit = {};
        
        if (options.body instanceof FormData) {
          retryHeaders = {
            'Authorization': `Bearer ${newToken}`
          };
        } else {
          retryHeaders = getAuthHeaders(newToken);
        }
        
        // Merge retry headers properly
        if (options.headers) {
          if (options.body instanceof FormData) {
            Object.entries(options.headers).forEach(([key, value]) => {
              if (key.toLowerCase() !== 'content-type') {
                (retryHeaders as Record<string, string>)[key] = value as string;
              }
            });
          } else {
            retryHeaders = {
              ...retryHeaders,
              ...options.headers
            };
          }
        }
        
        const retryResponse = await fetch(url, {
          ...options,
          headers: retryHeaders
        });
        
        // If still 401 after refresh, trigger logout
        if (retryResponse.status === 401) {
          console.log('Still 401 after refresh, triggering logout');
          window.dispatchEvent(new CustomEvent('token-expired'));
        }
        
        return retryResponse;
      } else {
        // Refresh failed, trigger logout
        console.log('Refresh failed, triggering logout');
        window.dispatchEvent(new CustomEvent('token-expired'));
      }
    }
  }

  if (response.status === 403) {
    console.warn('Received 403 - forcing logout flow');
    notifyForbiddenAccess(url);
  }
  
  return response;
};

// Convenience wrapper for GET requests
export const apiGet = (url: string, authRequired: boolean = true): Promise<Response> => {
  return apiRequest(url, { method: 'GET' }, authRequired);
};

// Convenience wrapper for POST requests
const isHeadersInit = (value: unknown): value is HeadersInit => {
  if (value instanceof Headers) return true;
  if (Array.isArray(value)) {
    return value.every((entry: unknown) =>
      Array.isArray(entry) && entry.length === 2 && entry.every((part: unknown) => typeof part === 'string')
    );
  }
  return isRecord(value) && Object.values(value).every(entry => typeof entry === 'string');
};

export const apiPost = (url: string, data: unknown, authRequired: boolean = true): Promise<Response> => {
  const hasCustomHeaders = isRecord(data) && Object.prototype.hasOwnProperty.call(data, 'header');
  let headers: HeadersInit = { 'Content-Type': 'application/json' };
  let body = data;
  if (hasCustomHeaders) {
    if (!isHeadersInit(data.header)) {
      throw new Error('Invalid custom API request headers');
    }
    headers = data.header;
    body = data.data;
  }
  return apiRequest(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(body)
  }, authRequired);
};

// Convenience wrapper for PUT requests
export const apiPut = (url: string, data: unknown, authRequired: boolean = true): Promise<Response> => {
  return apiRequest(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  }, authRequired);
};

// Convenience wrapper for PUT requests
export const apiPatch = (url: string, data: unknown, authRequired: boolean = true): Promise<Response> => {
  return apiRequest(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  }, authRequired);
};

// Convenience wrapper for DELETE requests
export const apiDelete = (url: string, authRequired: boolean = true): Promise<Response> => {
  return apiRequest(url, { method: 'DELETE' }, authRequired);
};
